import React from "react";

import { PlatformName } from "@common/models";
import { NetworkRequestType } from "@common/models/eventTracking";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { ActivityHistoryFilter } from "@main/pages/shared/consumers/useActivityHistoryFilter";
import { NetworkLogsFilterIcon } from "@main/pages/shared/icons/icons";

import * as Styled from "./NetworkFilter.styles";

const networkRequestTypeFilter: Record<keyof ActivityHistoryFilter["networkRequestTypes"], string> = {
  all: "All",
  fetch: "Fetch",
  document: "Doc",
  stylesheet: "CSS",
  script: "JS",
  font: "Font",
  image: "Img",
  media: "Media",
  manifest: "Manifest",
  ws: "WS",
  wasm: "Wasm",
  other: "Other",
};

const webOnlyKeys: Array<keyof ActivityHistoryFilter["networkRequestTypes"]> = [
  "document",
  "script",
  "stylesheet",
  "font",
  "manifest",
  "wasm",
];

const initialNetworkRequestTypes: Record<keyof ActivityHistoryFilter["networkRequestTypes"], boolean> = {
  all: true,
  fetch: false,
  document: false,
  stylesheet: false,
  script: false,
  font: false,
  image: false,
  media: false,
  manifest: false,
  ws: false,
  wasm: false,
  other: false,
};

interface NetworkFilterProps {
  setFilter: (types: ActivityHistoryFilter) => void;
  filter: ActivityHistoryFilter;
}

const NetworkFilter = ({ filter, setFilter }: NetworkFilterProps) => {
  const { selectedApp } = useAppSelectionContext();

  const { networkRequestTypes } = filter;

  const handleToggle = (key: keyof typeof networkRequestTypes) => {
    const isActive = networkRequestTypes[key];

    if (key === "all" && !isActive) {
      setFilter({ ...filter, networkRequestTypes: initialNetworkRequestTypes });
      return;
    }

    const updated = {
      ...networkRequestTypes,
      [key]: !isActive,
      all: false,
    };

    setFilter({
      ...filter,
      networkRequestTypes: updated,
    });
  };

  const handleErrorsOnly = () => {
    setFilter({
      ...filter,
      errorsOnly: !filter.errorsOnly,
    });
  };

  return (
    <Styled.Root>
      <Styled.NetworkRequestsIcon>
        <NetworkLogsFilterIcon />
      </Styled.NetworkRequestsIcon>
      {Object.entries(networkRequestTypeFilter).map((entry) => {
        const [key, label] = entry;
        const networkRequestFilterType = key as NetworkRequestType;

        const isWeb: boolean = selectedApp?.platform.name === PlatformName.WEB;
        if (!isWeb && webOnlyKeys.includes(networkRequestFilterType)) return null;

        return (
          <NetworkFilterToggle
            key={key}
            text={label}
            isActive={networkRequestTypes[networkRequestFilterType]}
            onClick={() => handleToggle(networkRequestFilterType)}
          />
        );
      })}
      <Styled.ErrorsCheckbox
        checked={filter.errorsOnly}
        label={"Errors only"}
        handleCheck={handleErrorsOnly}
      />
    </Styled.Root>
  );
};
interface NetworkFilterToggle {
  isActive: boolean;
  onClick: () => void;
  text: string;
}

const NetworkFilterToggle = ({ onClick, isActive, text }: NetworkFilterToggle) => {
  return (
    <Styled.NetworkFilterToggle
      onClick={onClick}
      $isActive={isActive}
    >
      <Styled.NetworkFilterToggleText>{text}</Styled.NetworkFilterToggleText>
    </Styled.NetworkFilterToggle>
  );
};

export { NetworkFilter };
